import { z } from "zod";
import { isPhoneComplete } from "@/shared/utils";

const baseServiceItemSchema = z.object({
  service_name: z.string().min(1, { message: "validation.legal.service_name_required" }),
  start_date: z.date().nullable(),
  end_date: z.date().nullable(),
  price: z.string().optional(),
  order: z.number().optional(),
});

const socialCategoryItemSchema = z.object({
  id: z.number().nullable().optional(),
  category_type: z.string(),
  document_file: z.instanceof(File).nullable(),
  document_file_url: z.string().nullable().optional(),
  issue_date: z.date().nullable(),
  expiry_date: z.date().nullable(),
});

export function createServiceContractSchema(_sharedPrice: boolean) {
  const baseSchema = {
    contract_city_id: z.number().optional(),
    contract_date: z.date().nullable().optional(),
    contract_end_date: z.date().nullable().optional(),
    executor_full_name: z.string().min(1, { message: "validation.legal.executor_full_name_required" }),
    executor_phone: z
      .string()
      .min(1, { message: "validation.legal.executor_phone_required" })
      .refine(isPhoneComplete, { message: "validation.legal.executor_phone_invalid" }),
    executor_iin: z
      .string()
      .min(1, { message: "validation.legal.executor_iin_required" })
      .regex(/^\d{12}$/, { message: "validation.legal.executor_iin_invalid" }),
    executor_email: z
      .string()
      .min(1, { message: "validation.legal.executor_email_required" })
      .email({ message: "validation.legal.executor_email_invalid" }),
    executor_bank_name: z.string().min(1, { message: "validation.legal.executor_bank_name_required" }),
    executor_iban: z.string().min(1, { message: "validation.legal.executor_iban_required" }),
    executor_bik: z.string().min(1, { message: "validation.legal.executor_bik_required" }),
    service_name: z.string().min(1, { message: "validation.legal.service_name_required" }),
    service_location: z.string().min(1, { message: "validation.legal.service_location_required" }),
    shared_price: z.boolean(),
    shared_start_date: z.date().nullable().optional(),
    shared_end_date: z.date().nullable().optional(),
    contract_amount: z.string().optional(),
    services: z
      .array(baseServiceItemSchema)
      .min(1, { message: "validation.legal.services_min_one" }),
    is_student: z.boolean(),
    enrollment_start_date: z.date().nullable(),
    enrollment_end_date: z.date().nullable(),
    enrollment_verification_file: z.instanceof(File).nullable(),
    enrollment_verification_file_url: z.string().nullable().optional(),
    has_social_categories: z.boolean(),
    social_categories: z.array(socialCategoryItemSchema),
  };

  return z.object(baseSchema).superRefine((data, ctx) => {
    const isSharedPrice = data.shared_price ?? false;

    data.services.forEach((service, index) => {
      if (!isSharedPrice) {
        if (!service.start_date) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "validation.legal.service_start_date_required",
            path: ["services", index, "start_date"],
          });
        }
        if (!service.end_date) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "validation.legal.service_end_date_required",
            path: ["services", index, "end_date"],
          });
        }
        if (!service.price || service.price.trim() === "") {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "validation.legal.service_price_required",
            path: ["services", index, "price"],
          });
        } else if (!/^\d+(\.\d+)?$/.test(service.price)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "validation.legal.service_price_invalid",
            path: ["services", index, "price"],
          });
        }
      }
    });

    if (isSharedPrice) {
      if (!data.shared_start_date) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "validation.legal.service_start_date_required",
          path: ["shared_start_date"],
        });
      }
      if (!data.shared_end_date) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "validation.legal.service_end_date_required",
          path: ["shared_end_date"],
        });
      }
      if (!data.contract_amount || data.contract_amount.trim() === "") {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "validation.legal.contract_amount_required",
          path: ["contract_amount"],
        });
      } else if (!/^\d+(\.\d+)?$/.test(data.contract_amount)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "validation.legal.contract_amount_invalid",
          path: ["contract_amount"],
        });
      }
    }

    if (data.is_student) {
      if (!data.enrollment_start_date) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "executor.errors.enrollmentStartDateRequired",
          path: ["enrollment_start_date"],
        });
      }
      if (!data.enrollment_end_date) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "executor.errors.enrollmentEndDateRequired",
          path: ["enrollment_end_date"],
        });
      }
      if (
        data.enrollment_start_date &&
        data.enrollment_end_date &&
        data.enrollment_end_date <= data.enrollment_start_date
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "executor.errors.enrollmentEndDateBeforeStart",
          path: ["enrollment_end_date"],
        });
      }
      if (!data.enrollment_verification_file && !data.enrollment_verification_file_url) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "executor.errors.enrollmentFileRequired",
          path: ["enrollment_verification_file"],
        });
      }
    }

    if (data.has_social_categories) {
      data.social_categories.forEach((category, index) => {
        if (!category.category_type) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "executor.errors.categoryTypeRequired",
            path: ["social_categories", index, "category_type"],
          });
        }
        if (!category.document_file && !category.document_file_url) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "executor.errors.categoryDocumentRequired",
            path: ["social_categories", index, "document_file"],
          });
        }
      });
    }
  });
}

export const serviceContractSchema = createServiceContractSchema(false);
